import React, { useState } from 'react';

import formPreview from 'assets/images/cards_type/form_1.jpg';
import infoPreview from 'assets/images/cards_type/info_1.jpg';
import personalityPreview from 'assets/images/cards_type/pq_1.jpg';
import pollPreview from 'assets/images/cards_type/poll_1.jpg';
import registrationPreview from 'assets/images/cards_type/reg_1.jpg';
import triviaPreview from 'assets/images/cards_type/trv_2.jpg';
import PageLayout from 'components/common/PageLayout';
import { ENTERPRISE_CONTACT_LINK, FLOW_ROUTE, SUBSCRIPTION_PLANS_PAGE } from 'components/constants';

import css from './CardsFormat.module.scss';

type JourneyStage = {
  eyebrow: string;
  title: string;
  text: string;
  formats: string[];
  image: string;
  imageAlt: string;
};

const journeyStages: JourneyStage[] = [
  {
    eyebrow: '01',
    title: 'Engage',
    text: 'Open with content, media or an interaction that earns attention and gives people a reason to continue.',
    formats: ['Interactive content', 'Video', 'Games', 'Calls to action'],
    image: infoPreview,
    imageAlt: 'Interactive information experience built in Storycards',
  },
  {
    eyebrow: '02',
    title: 'Understand',
    text: 'Ask the right questions and capture choices, preferences and intent as structured first-party data.',
    formats: ['Questions', 'Polls', 'Trivia', 'Visual choices', 'Open inputs'],
    image: triviaPreview,
    imageAlt: 'Interactive trivia experience built in Storycards',
  },
  {
    eyebrow: '03',
    title: 'Personalize',
    text: 'Use answers, known data and conditions to adapt the content and route every person through the right path.',
    formats: ['Conditional paths', 'Dynamic content', 'Validated inputs', 'Known data'],
    image: formPreview,
    imageAlt: 'Interactive lead form built in Storycards',
  },
  {
    eyebrow: '04',
    title: 'Recommend',
    text: 'Combine scoring and flow logic to match each user with the most relevant product, content or next action.',
    formats: ['Personality assessments', 'Product matching', 'Scored quizzes', 'Personalized results'],
    image: personalityPreview,
    imageAlt: 'Personalized assessment built in Storycards',
  },
  {
    eyebrow: '05',
    title: 'Complete',
    text: 'Turn the journey into a completed action with a result, registration, handoff or personalized next step.',
    formats: ['Registration', 'Results', 'Lead handoff', 'Redirects & next actions'],
    image: registrationPreview,
    imageAlt: 'Completed interactive journey built in Storycards',
  },
];

const principles = [
  {
    eyebrow: '01',
    title: 'Built for interaction',
    text: 'Every card can present, ask, validate, calculate, capture or respond.',
  },
  {
    eyebrow: '02',
    title: 'Flexible flow logic',
    text: 'Reorder steps, create branches and conditions, and connect any interaction into a complete journey.',
  },
  {
    eyebrow: '03',
    title: 'See where users drop off',
    text: 'Track engagement and completion across every step, not only the final result.',
  },
  {
    eyebrow: '04',
    title: 'Improve without rebuilding',
    text: 'Change individual steps, test alternatives and continuously optimize the journey.',
  },
];

const products = [
  { title: 'Product advisors', text: 'Help customers understand their needs and reach a more relevant product or service.' },
  { title: 'Quizzes & assessments', text: 'Turn questions, scoring and feedback into an engaging, useful experience.' },
  { title: 'Lead qualification', text: 'Collect context and intent before routing each lead to the right next step.' },
  { title: 'Registration & onboarding', text: 'Guide users through information, decisions and required inputs in one clear journey.' },
  { title: 'Interactive content', text: 'Combine stories, media and audience participation inside a branded digital product.' },
  { title: 'Audience research', text: 'Capture declared preferences and structured first-party data through valuable interactions.' },
];

const enterprisePoints = [
  { title: 'Structured event data', text: 'Every view, answer, interaction and completion is captured in a consistent way.' },
  { title: 'Live funnel visibility', text: 'Understand engagement, completion and drop-off at every point in the journey.' },
  { title: 'Responsive and on brand', text: 'Control every layout, breakpoint and visual detail in the Studio.' },
  { title: 'Governed at scale', text: 'Manage reusable components, permissions and live products from one platform.' },
];

const CardsFormat: React.FC = () => {
  const [activeFormat, setActiveFormat] = useState(0);
  const selectedFormat = journeyStages[activeFormat];

  return (
    <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
      <main className={css.page}>
        <section className={css.hero}>
          <div className={css.heroInner}>
            <div className={css.heroCopy}>
              <p className={css.eyebrow}>Engagement &amp; flow engine</p>
              <h1>Every screen moves the journey forward.</h1>
              <p className={css.heroText}>
                Storycards turns every interaction into a measurable step in a connected journey. Combine content,
                questions, forms, logic and personalized outcomes to capture better data, identify drop-offs and
                improve completion.
              </p>
              <div className={css.heroActions}>
                <a className={css.primaryCta} href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer">
                  Start creating <span aria-hidden="true">→</span>
                </a>
                <a className={css.textCta} href="#formats">See how journeys work</a>
              </div>
            </div>

            <div className={css.heroVisual} aria-label="Examples of interactive Storycards formats">
              <figure className={css.heroPreviewLarge}>
                <img src={personalityPreview} alt="Interactive product matching experience" />
              </figure>
              <figure className={css.heroPreviewTop}>
                <img src={registrationPreview} alt="Interactive registration experience" />
              </figure>
              <figure className={css.heroPreviewBottom}>
                <img src={pollPreview} alt="Interactive poll experience" />
              </figure>
              <span className={css.heroConnectorOne} aria-hidden="true" />
              <span className={css.heroConnectorTwo} aria-hidden="true" />
            </div>
          </div>
        </section>

        <section className={css.definition}>
          <div className={css.sectionInner}>
            <h2>Not predefined experiences. Building blocks for journeys that perform.</h2>
            <p>
              AI can create any experience. Storycards gives every screen a purpose, connects it to a measurable flow
              and keeps the entire product flexible, editable and ready to improve.
            </p>
          </div>
        </section>

        <section className={css.relationship}>
          <div className={css.sectionInner}>
            <header className={css.relationshipHeader}>
              <p className={css.eyebrow}>Structure beneath the creativity</p>
              <h2>Creative freedom, with the structure performance needs.</h2>
            </header>
            <div className={css.relationshipGrid}>
              {principles.map((principle) => (
                <article key={principle.title}>
                  <span>{principle.eyebrow}</span>
                  <h3>{principle.title}</h3>
                  <p>{principle.text}</p>
                </article>
              ))}
            </div>
            <a href={FLOW_ROUTE} className={css.darkCta}>
              Explore smart flows <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        <section className={css.formats} id="formats">
          <div className={css.sectionInner}>
            <div className={css.sectionHeader}>
              <h2>One journey. Every step can adapt.</h2>
              <p>
                Engage, understand, personalize, recommend and complete. Replace any step with the interaction that
                serves the user and the product best.
              </p>
            </div>

            <div className={css.formatExplorer}>
              <div className={css.formatNav} role="tablist" aria-label="Stages in an interactive journey">
                {journeyStages.map((group, index) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeFormat === index}
                    aria-controls="format-preview"
                    className={activeFormat === index ? css.activeFormat : ''}
                    onClick={() => setActiveFormat(index)}
                    key={group.title}
                  >
                    <span>{group.eyebrow}</span>
                    <div>
                      <strong>{group.title}</strong>
                      <p>{group.text}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className={css.formatPreview} id="format-preview" role="tabpanel" key={selectedFormat.title}>
                <img src={selectedFormat.image} alt={selectedFormat.imageAlt} />
                <div className={css.formatTags}>
                  {selectedFormat.formats.map((format) => <span key={format}>{format}</span>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={css.products}>
          <div className={css.sectionInner}>
            <div className={css.sectionHeader}>
              <h2>One engine. Many product outcomes.</h2>
              <p>Combine the right interactions and logic into a useful, branded product built around a real goal.</p>
            </div>
            <div className={css.productGrid}>
              {products.map((product, index) => (
                <article key={product.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{product.title}</h3>
                  <p>{product.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={css.enterprise}>
          <div className={css.sectionInner}>
            <div className={css.sectionHeader}>
              <h2>Built to measure and improve every journey.</h2>
              <p>
                The system understands the role of every step, generates consistent event data and gives teams the
                visibility to improve performance without rebuilding the product.
              </p>
            </div>
            <div className={css.enterpriseGrid}>
              {enterprisePoints.map((point) => (
                <article key={point.title}>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={css.cta}>
          <div className={css.ctaInner}>
            <h2>Build journeys that learn and perform.</h2>
            <div>
              <p>Create freely while Storycards connects every interaction to data, logic and measurable outcomes.</p>
              <div className={css.ctaActions}>
                <a className={css.primaryCta} href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer">
                  Start creating <span aria-hidden="true">→</span>
                </a>
                <a className={css.textCta} href={ENTERPRISE_CONTACT_LINK}>Talk to our enterprise team</a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
};

export default CardsFormat;
